import React from 'react'
import Select from 'react-select'
import PropTypes from 'prop-types'

import {
  StyledMenu,
  StyledOption,
  StyledControl,
  StyledValueContainer,
  StyledSingleValue,
  StyledPlaceholder,
  StyledInput,
  StyledGroup,
  NoOptionsMessage,
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator,
  StyledLoadingIndicator,
  StyledError,
} from './autocomplete.component.styles'

const components = {
  Menu: StyledMenu,
  Control: StyledControl,
  ValueContainer: StyledValueContainer,
  SingleValue: StyledSingleValue,
  Option: StyledOption,
  Input: StyledInput,
  Group: StyledGroup,
  NoOptionsMessage,
  Placeholder: StyledPlaceholder,
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator,
  LoadingIndicator: StyledLoadingIndicator,
}

class AutoComplete extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    maxHeight: PropTypes.number,
    menuWidth: PropTypes.string,
    onChange: PropTypes.func,
    onQuery: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onInputChange: PropTypes.func,
    value: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
    defaultOptions: PropTypes.oneOfType([
      PropTypes.shape({
        then: PropTypes.func,
        catch: PropTypes.func,
      }),
      PropTypes.array,
    ]),
    customOptionComponent: PropTypes.func,
    showOptionSeparators: PropTypes.bool,
    meta: PropTypes.object,
    displayKey: PropTypes.string,
    groupSettings: PropTypes.arrayOf(
      PropTypes.shape({
        identifier: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        customOptionComponent: PropTypes.func,
      })
    ),
    isLoading: PropTypes.bool,
    dataTestid: PropTypes.string,
    supportEditSelectedValue: PropTypes.bool,
    getValueFromOnChangeSelection: PropTypes.func,
    defaultOptionsGroupIdentifier: PropTypes.string
  }

  static defaultProps = {
    onInputChange: () => {},
    getValueFromOnChangeSelection: () => {}
  }

  constructor(props) {
    super(props)
    this.state = {
      inputValue: props.supportEditSelectedValue ? (props.value?.value || '') : '',
      loadedOptions: null,
      selectedOption: null,
      defaultOptions: null,
      isLoading: props.isLoading || false,
    }
    Promise.resolve(props.defaultOptions)
      .then(defaultOptions => {
        const nextOptions = this.props.groupSettings
          ? this._createGroupedOptions(defaultOptions, true)
          : defaultOptions

        this.setState({ defaultOptions: nextOptions })
      })
      .catch(() => {
        this.setState({ defaultOptions: null })
      })
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { value: nextValueObj } = nextProps
    const { value: prevValueObj, supportEditSelectedValue } = this.props
    const nextValue = nextValueObj && nextValueObj.value
    const prevValue = prevValueObj && prevValueObj.value

    if (supportEditSelectedValue) {
      if (nextValue && nextValue !== prevValue) {
        this.setState({ inputValue: nextValue })
      }
    } else {
      const inputValue = prevValue !== nextValue && nextValue === ''
        ? { inputValue: nextValue }
        : {}
      this.setState({ ...inputValue })
    }
    this.setState({ isLoading: nextProps.isLoading })
  }

  onInputChangeHandler = (value, { action }) => {
    const { supportEditSelectedValue, resetFieldsOnChange, onChange, onInputChange } = this.props
    const setInputValueAndQuery = newValue => {
      this.setState({ inputValue: newValue })
      this.queryFunction(newValue)
    }

    if(supportEditSelectedValue) {
      if(action === 'input-change') {
        setInputValueAndQuery(value)
        onInputChange(value)
        if(resetFieldsOnChange) {
          onChange(null)
        }
      }
    } else {
      switch (action) {
        case 'set-value':
          setInputValueAndQuery(value)
          break
        case 'input-change':
          setInputValueAndQuery(value)
          onInputChange(value)
          break
        default:
          break
      }
    }
  }

  queryFunction = queryValue => {
    if (this.props.onQuery && queryValue !== '') {
      this.setState({ isLoading: true })
      Promise.resolve(this.props.onQuery(queryValue))
        .then(loadedOptions => {
          if (this.state.inputValue !== queryValue) {
            return
          }

          const nextOptions = this.props.groupSettings
            ? this._createGroupedOptions(loadedOptions)
            : loadedOptions

          this.setState({
            loadedOptions: nextOptions,
            isLoading: false,
          })
        })
    } else {
      this.setState({
        loadedOptions: null,
        isLoading: false,
      })
    }
  }

  /**
   *
   * @param {{ [id: string]: [] } nextOptions
   */
  _createGroupedOptions(nextOptions, showDefaultOptions = false) {
    const { groupSettings, defaultOptionsGroupIdentifier } = this.props

    if (!nextOptions) {
      return []
    }

    const filteredList = showDefaultOptions
      ? groupSettings.filter(group => group.identifier === defaultOptionsGroupIdentifier)
      : groupSettings.filter(group => group.identifier !== defaultOptionsGroupIdentifier)

    return filteredList.map(group => ({
      identifier: group.identifier,
      label: group.label,
      options: nextOptions[group.identifier],
      customOptionComponent: group.customOptionComponent,
    }))
  }

  render() {
    const {
      id,
      name,
      className,
      onChange,
      onBlur,
      onFocus,
      placeholder,
      value,
      customOptionComponent,
      meta = {},
      displayKey,
      groupSettings,
      maxHeight,
      menuWidth,
      dataTestid,
      showOptionSeparators,
      supportEditSelectedValue,
      getValueFromOnChangeSelection,
    } = this.props

    const {
      inputValue,
      defaultOptions,
      loadedOptions,
      isLoading,
    } = this.state

    const options = loadedOptions || defaultOptions || []
    let wrappedOnChange


    if(supportEditSelectedValue) {
      wrappedOnChange = selected => {
        const updatedInputvalue = getValueFromOnChangeSelection(selected) || ''
        onChange(selected || null)
        this.setState({ inputValue: updatedInputvalue })
      }
    } else {
      wrappedOnChange = typeof onChange === 'function'
        ? selected => onChange(selected || null)
        : null
    }
    /**
     * Redux-Form expects the value to be passed-through when onBlur is called
     * @see https://github.com/erikras/redux-form/issues/2768
     */
    const wrappedOnBlur = typeof onBlur === 'function'
      ? () => {
        onBlur(value)
      }
      : null

    const error = meta && meta.touched && meta.error && !isLoading

    return (
      <div className={className} data-testid={dataTestid}>
        <Select
          id={id}
          name={name}
          options={options}
          inputValue={inputValue}
          value={value && value.value ? value : null}
          components={components}
          placeholder={placeholder}
          error={error}
          customOptionComponent={customOptionComponent || null}
          onInputChange={this.onInputChangeHandler}
          onChange={wrappedOnChange}
          onBlur={wrappedOnBlur}
          onFocus={onFocus}
          filterOption={this.props.onQuery ? () => true : undefined}
          displayKey={displayKey}
          groupSettings={groupSettings}
          maxMenuHeight={maxHeight}
          menuWidth={menuWidth}
          isClearable
          isLoading={isLoading}
          showOptionSeparators={showOptionSeparators}
        />
        <StyledError error={error} >{meta.error}</StyledError>
      </div>
    )
  }
}

AutoComplete.displayName = 'AutoComplete'

export {
  AutoComplete,
}
